// src/components/DetailUnifiedBar.jsx
//
// Shared unified bar: absolute-left back link + centred tab strip.
// Extracted from LocationDetail's location-unified-bar so that report
// detail pages (HeadcountTrendReport) can reuse the same layout chrome.
//
// Layout invariant (matches LocationDetail Zone 2):
//   • Outer: position:relative, shared compact tab/back/pager height
//   • Back button: position:absolute, left:16, width:90
//   • Inner (bar-content): width:100%, maxWidth:1400, margin:0 auto,
//     flex, paddingLeft = max(32px, 106 − max(0,(cqw−1400)/2))
//     so tabs align with the Property pane left edge (32 px floor)
//     and slide rightward only when the back-link would overlap.
//
// Props:
//   backLabel      — text after "‹" (e.g. "Reports", "Locations")
//   backAriaLabel  — optional accessible name override for the back button
//   onBack         — click handler for the back button
//   backTestId     — data-testid for the back button (default: "detail-back-button")
//   tabs           — [{key, label}] ordered list of tabs to render
//   activeTab      — key of the currently active tab
//   onTabChange    — called with the new tab key when a tab is clicked
//   tabBadge       — optional fn(key) → number; renders a red badge when > 0
//   rightSlot      — optional ReactNode rendered after the tabs in the centred flow
//                    (e.g. pager + edit buttons for LocationDetail)
//   testId         — data-testid for the outer bar div (default: "detail-unified-bar")
//   barContentTestId — data-testid for the centred inner div (default: "detail-bar-content")

import React from "react";
import { T } from "../app/tokens.js";
import { ChromeTab } from "./ObjectChrome.jsx";
import { RecordNavigationPortal } from "./OneRowChrome.jsx";

export function DetailUnifiedBar({
  backLabel = "Back",
  backAriaLabel,
  onBack,
  backTestId = "detail-back-button",
  tabs = [],
  activeTab,
  onTabChange = () => {},
  tabBadge,
  rightSlot = null,
  testId = "detail-unified-bar",
  barContentTestId = "detail-bar-content",
  recordNavigation = false,
  oneRow = false,
}) {
  if (oneRow) return (
    <>
      <RecordNavigationPortal
        back={<button className="one-row-back-link" data-testid={backTestId}
          aria-label={backAriaLabel ?? `‹ ${backLabel}`} onClick={onBack}>‹ {backLabel}</button>}
        actions={rightSlot}
      />
      {tabs.length > 0 && <nav data-testid={testId} className="one-row-record-tabs" aria-label="Record">
        <div data-testid={barContentTestId} className="object-subnav-tabs">
          {tabs.map((tab) => <ChromeTab key={tab.key} active={activeTab === tab.key}
            onClick={() => onTabChange(tab.key)} data-testid={`tab-button-${tab.key}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, transition: "all 0.2s" }}>
            {tab.label}
            {tabBadge?.(tab.key) > 0 && <span data-testid={`tab-badge-${tab.key}`}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 16, height: 16, borderRadius: 8, background: T.red, color: T.white,
                fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "0 4px", lineHeight: 1 }}>
              {tabBadge(tab.key)}
            </span>}
          </ChromeTab>)}
        </div>
      </nav>}
    </>
  );
  return (
    <div
      data-testid={testId}
      className={tabs.length ? "object-subnav-row object-subnav-inner" : "object-detail-back-only"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        position: "relative",
        containerType: "inline-size",
      }}
    >
      {/* Back link — pinned to absolute far-left, outside flow */}
      <button
        onClick={onBack}
        data-testid={backTestId}
         aria-label={backAriaLabel ?? `‹ ${backLabel}`}
        style={{
          position: "absolute",
          left: 16,
          top: 0,
          bottom: 0,
          width: 90,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "0 8px",
          color: T.blue,
          fontSize: T.fsSm,
          fontWeight: T.fwSemibold,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
        }}
      >
        ‹ {backLabel}
      </button>

      {/* Centred wrapper — tabs align with Property pane left edge (32 px floor).
          paddingLeft = max(32px, 106 − max(0,(cqw−1400)/2)):
            • cqw≥1612: padding=32px → first tab aligned with pane left edge.
            • cqw<1612: padding grows → first tab stays clear of back-link.
          containerType:inline-size on parent means 100cqw = bar width. */}
      <div
        data-testid={barContentTestId}
        style={{
          width: "100%",
          maxWidth: 1400,
          margin: "0 auto",
          display: "flex",
          alignItems: "stretch",
          minWidth: 0,
          paddingLeft: "max(32px, calc(106px - max(0px, (100cqw - 1400px) / 2)))",
        }}
      >
        <div className="object-subnav-tabs">
        {tabs.map((tab) => {
          const active = activeTab === tab.key;
          const badge  = tabBadge ? tabBadge(tab.key) : undefined;
          return (
            <ChromeTab active={active}
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              data-testid={`tab-button-${tab.key}`}
              style={{
                transition: "all 0.2s",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {tab.label}
              {badge > 0 && (
                <span
                  data-testid={`tab-badge-${tab.key}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    background: T.red,
                    color: T.white,
                    fontSize: T.fsXxs,
                    fontWeight: T.fwBold,
                    padding: "0 4px",
                    lineHeight: 1,
                  }}
                >
                  {badge}
                </span>
              )}
            </ChromeTab>
          );
        })}
        </div>
        {rightSlot && (
          <div className={recordNavigation ? "object-record-actions" : undefined}
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
            {rightSlot}
          </div>
        )}
      </div>
    </div>
  );
}
